import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectAuth } from 'services/auth/redux/selectors'
import { showErrorToast } from 'services/toast/redux/actions'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { fromJS, Map } from 'immutable'
import _debounce from 'lodash/debounce'
import { useQuery, useLazyQuery } from '@apollo/client'
import { Alert, Grid, Box } from '@mui/material'
import { MEDIA_ASSET_PROVIDER_BRIGHTCOVE_STUDIO_VIDEOS } from 'services/media-assets/provider-urls'
import {
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE,
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
} from 'services/media-assets/reference-types'
import { getMediaInfo } from 'services/media'
import MuiButton from 'gui/components/common/MuiButton'
import VideoPlayer from 'gui/components/video-player'
import Link from 'gui/components/link'
import { TYPE_BRIGHTCOVE_STUDIO } from 'gui/components/link/types'
import MediaAutoComplete from 'gui/components/media-auto-complete'
import { Media, MediaAsset } from '@/__generated__/gql/graphql'
import { GET_MEDIA_BY_ID, GET_MEDIA_SUGGESTIONS } from './queries'
import { getVideoUrl, isMediaPlayable } from '../mediaUtils'
import { getDateWithoutTime } from '../helpers'
import '../index.scss'

type MediaData = {
  mediaList?: {
    media?: Media[] | null
  } | null
}

type MediaInfo = {
  mediaUrls: Record<string, string> | null
  textTracks: {
    thumbnail: string | null
  } | null
}

interface ContentMediaFieldProps {
  name: string
  mediaId: string | null
  onChange: (
    event: {
      target: { name: string; value: string | null }
    },
    record?: { contentId: string; mediaId: string },
  ) => void
  disabled: boolean
  clearMediaField: (name: string) => void
}

/**
 * Renders a single media field with autocomplete, preview, and metadata.
 */
function ContentMediaField ({
  name,
  mediaId,
  onChange,
  disabled,
  clearMediaField,
}: ContentMediaFieldProps) {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [notPlayable, setNotPlayable] = useState(false)
  const [loadingMediaInfo, setLoadingMediaInfo] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [suggestions, setSuggestions] = useState<Partial<Media>[]>([])
  const [mediaInfo, setMediaInfo] = useState<MediaInfo | null>(null)

  const [queryMediaSuggestions, { data: mediaSuggestionsData, loading: loadingMediaSuggestions }] =
    useLazyQuery<MediaData>(GET_MEDIA_SUGGESTIONS, {
      variables: { limit: 25 },
    })

  // Debounce the query media suggestions
  const queryMediaSuggestionsDebounced = useRef(
    _debounce((value: string) => {
      if (value) {
        queryMediaSuggestions({ variables: { contentId: `%${value}%`, limit: 25 } })
      } else {
        setSuggestions([])
      }
    }, 300),
  )

  // Fetch media by mediaId when mediaId changes
  const {
    data: mediaData,
    loading: loadingMedia,
    refetch: refetchMedia,
  } = useQuery<MediaData>(GET_MEDIA_BY_ID, {
    variables: { mediaId },
    skip: !mediaId,
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      dispatch(showErrorToast(`Loading media failed: ${errorMessage}`))
    },
  })

  // Fetch media info (providerKey, mediaUrls, textTracks)
  const getMediaInfoData = useCallback(async () => {
    if (!mediaId) {
      setMediaInfo(null)
      setLoadingMediaInfo(false)
      return
    }
    setLoadingMediaInfo(true)
    const mediaInfoData = await getMediaInfo({
      auth,
      server: brooklynUrl,
      mediaId: Number(mediaId),
    })
    setMediaInfo(mediaInfoData.toJS())
    setLoadingMediaInfo(false)
  }, [auth, brooklynUrl, mediaId])

  const media = useMemo(() => {
    const mediaList = mediaData?.mediaList?.media
    return mediaList && mediaList.length > 0 ? mediaList[0] : null
  }, [mediaData])

  // Get providerKey from mediaAssets (first asset with providerKey)
  const providerKey = useMemo(() => {
    const mediaAsset = media?.mediaAssets?.find((asset: MediaAsset | null) => asset?.providerKey)
    return mediaAsset?.providerKey || null
  }, [media])

  const mediaAssets = useMemo(() => media?.mediaAssets || [], [media])

  // Subtitles must be passed to VideoPlayer as an immutable list
  const subtitles = useMemo(
    () =>
      fromJS(
        mediaAssets.filter(
          (asset: MediaAsset | null) =>
            asset?.mediaReferenceType === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE,
        ),
      ),
    [mediaAssets],
  )

  // Captions must be passed to VideoPlayer as an immutable list
  const captions = useMemo(
    () =>
      fromJS(
        mediaAssets.filter(
          (asset: MediaAsset | null) =>
            asset?.mediaReferenceType === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
        ),
      ),
    [mediaAssets],
  )

  // Media URLs must be passed to VideoPlayer as an immutable map
  const mediaUrls = fromJS(mediaInfo?.mediaUrls ?? Map())
  const textTracksThumbnail = mediaInfo?.textTracks?.thumbnail ?? null
  const videoUrl = getVideoUrl(mediaUrls)

  const metaData = useMemo(
    () => [
      {
        label: 'Media ID',
        value: (
          <Link absolute={true} href={`/media/${mediaId}`}>
            {mediaId}
          </Link>
        ),
      },
      {
        label: 'BC Video ID',
        value: providerKey && (
          <Link
            className="media-detail__provider-link"
            type={TYPE_BRIGHTCOVE_STUDIO}
            href={`${MEDIA_ASSET_PROVIDER_BRIGHTCOVE_STUDIO_VIDEOS}/${providerKey}`}
          >
            {providerKey}
          </Link>
        ),
      },
      {
        label: 'Media Maestro Code',
        value: media?.mediaMaestroTitleCode,
      },
      {
        label: 'Policy ID',
        value: media?.policyId,
      },
      {
        label: 'Video Updated at',
        value: getDateWithoutTime(media?.updatedAt),
      },
      {
        label: 'Duration',
        value: media?.duration,
      },
    ],
    [media, mediaId, providerKey],
  )

  const handleSuggestionSelect = (
    _event: React.ChangeEvent<HTMLInputElement>,
    record: { contentId: string; mediaId: string },
  ) => {
    if (record && record.mediaId) {
      onChange({ target: { name, value: record.mediaId } }, record)
      setSearchValue(record.contentId)
      setSuggestions([])
      // Refetch media data
      if (record.mediaId) {
        refetchMedia({ mediaId: record.mediaId })
      }
    } else {
      onChange({ target: { name, value: null } })
      setSearchValue('')
      setSuggestions([])
    }
  }

  const handleRemoveMedia = () => {
    onChange({ target: { name, value: null } })
    clearMediaField(name)
    setSearchValue('')
    setNotPlayable(false)
  }

  const handleAutocompleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSearchValue(value)
    queryMediaSuggestionsDebounced.current(value.trim())
  }

  // Update suggestions when search value changes
  useEffect(() => {
    setSuggestions(mediaSuggestionsData?.mediaList?.media ?? [])
  }, [mediaSuggestionsData])

  // Fetch media info when mediaId changes
  useEffect(() => {
    if (mediaId) {
      getMediaInfoData()
    }
  }, [mediaId, getMediaInfoData])

  // Update search value when contentId changes
  useEffect(() => {
    if (media?.contentId) {
      setSearchValue(media.contentId)
      setSuggestions([])
    }
  }, [media?.contentId])

  // Check if media is playable
  useEffect(() => {
    if (!mediaId || !media) {
      setNotPlayable(false)
      return
    }
    setNotPlayable(!isMediaPlayable(fromJS(media), videoUrl))
  }, [mediaId, media, videoUrl])

  const isLoading = loadingMedia || loadingMediaInfo || loadingMediaSuggestions

  return (
    <div key={`content-media-${name}`}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <MediaAutoComplete
            value={searchValue}
            name={name}
            onChange={handleAutocompleteChange}
            onSuggestionSelect={handleSuggestionSelect}
            disabled={disabled}
            placeholder={'Search or select media...'}
            suggestions={suggestions}
          />
        </Grid>
      </Grid>
      {mediaId && notPlayable && !isLoading && (
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Alert severity="warning">
              Warning: This media is not playable. Please ensure the media has valid video URLs
              before publishing.
            </Alert>
          </Grid>
        </Grid>
      )}
      {mediaId && !isLoading && media && (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={7} lg={8}>
            <VideoPlayer
              src={getVideoUrl(mediaUrls)}
              srcTextTrackThumnail={textTracksThumbnail}
              subtitles={subtitles}
              captions={captions}
            />
          </Grid>
          <Grid item xs={12} md={5} lg={4}>
            <Box className="modern-info-box">
              {metaData.map(({ label, value }) => (
                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} key={label}>
                  <Grid item xs={5} sx={{ fontWeight: 500, color: '#555' }}>
                    {label}
                  </Grid>
                  <Grid item xs={7} sx={{ pl: 2 }}>
                    {value || <span style={{ color: '#aaa' }}>—</span>}
                  </Grid>
                </Grid>
              ))}
              {!disabled && (
                <Box sx={{ textAlign: 'right', mt: 2 }}>
                  <MuiButton color="error" style={{ marginTop: 8 }} onClick={handleRemoveMedia}>
                    Remove Media
                  </MuiButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default ContentMediaField
